/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import {
    IBuyboxAddToCartViewProps,
    IBuyboxAddToOrderTemplateViewProps,
    IBuyboxAddToWishlistViewProps,
    IBuyboxCallbacks,
    IBuyboxData,
    IBuyboxFindInStoreViewProps,
    IBuyboxKeyInPriceViewProps,
    IBuyboxProductConfigureDropdownViewProps,
    IBuyboxProductConfigureViewProps,
    IBuyboxProductQuantityViewProps,
    IBuyboxProps,
    IBuyboxShopSimilarLookViewProps,
    IBuyboxState,
    IBuyboxViewProps
} from '@msdyn365-commerce-modules/buybox';
import { IncrementalQuantity, ITelemetryContent, Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import {
    IBuyboxProps as IBuyboxExtentionProps,
    IBuyboxResources as IBuyboxExtentionResources
} from '../definition-extensions/buybox.ext.props.autogenerated';
//added by Pingala 
import { ArrayExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { AsyncResult, AttributeValue } from '@msdyn365-commerce/retail-proxy';
export interface IBuyboxExtData {
    productAttributes : AsyncResult<AttributeValue[]>;
}

import {
    INTENCITY_ATTRIBUTE_NAME,
    COMPATABILITY_ATTRIBUTE_NAME,
    DESCRIPTION_ATTRIBUTE_NAME,
    FORMAT_ATTRIBUTE_NAME
} from '../../../constants';

const BuyboxView: React.FC<IBuyboxViewProps 
                            & IBuyboxExtentionProps<IBuyboxData> 
                            & IBuyboxExtentionProps<IBuyboxExtData>> = props => {
    const {
        ModuleProps,
        MediaGalleryContainerProps,
        ProductInfoContainerProps,
        addToCart,
        addToOrderTemplate,
        addToWishlist,
        telemetryContent,
        configure,
        //description,
        findInStore,
        quantity,
        price,
        // title,
        rating,
        callbacks,
        state,
        resources,
        inventoryLabel,
        shopSimilarLook,
        keyInPrice,
        quantityLimitsMessages,
        min,
        max,
        applyDefaultOrderSettings,
        shopSimilarDescription
        //unitOfMeasure
    } = props;      
    return (
        <Module {...ModuleProps}>
            <Node {...MediaGalleryContainerProps}>{props.mediaGallery}</Node>
            <Node {...ProductInfoContainerProps}>
                {/* {title} commented by Pingala*/}
                {getProductAttribute(props, ('Short Name'))}
                {price}
                {/* {unitOfMeasure} */}
                {getProductAttribute(props, ('Cup Size'))}
                {props.bulkPurchaseLink}
                {/* {description} commented by Pingala*/}
                {/* added by Pingala  */}
                {getProductAttribute(props, DESCRIPTION_ATTRIBUTE_NAME)}
                {/* end comments  */}
                {/* {_renderTextBlock(props.slots.textBlocks)}  */}
                {getProductAttribute(props,COMPATABILITY_ATTRIBUTE_NAME)}
                {/* {getProductAttribute(props, ("Intensity"))} */}
                {getProductIntensity(props,INTENCITY_ATTRIBUTE_NAME)}
                {getProductAttribute(props, FORMAT_ATTRIBUTE_NAME)}
                {rating}
                {configure && _renderConfigure(configure)}
                {keyInPrice && _renderKeyInPrice(keyInPrice)}
                {quantity &&
                    _renderQuantity(
                        quantity,
                        callbacks,
                        props,
                        state,
                        resources,
                        quantityLimitsMessages,
                        min,
                        max,
                        applyDefaultOrderSettings,
                        telemetryContent
                    )}
                {inventoryLabel}
                {_renderCartAndActions(addToCart, addToOrderTemplate, addToWishlist)}
                {findInStore && _renderFindInStore(findInStore)}
                {_renderSocialShare(props.slots && props.slots.socialShare)}
                {shopSimilarLook && _renderShopSimilarItem(shopSimilarLook)}
                {shopSimilarDescription && _renderShopSimilarItem(shopSimilarDescription)}
            </Node>
        </Module>
    );
};

// added by Pingala 

const MAX_INTENSITY = "MAX-INTENSITY";

export function getMaxIntensity(
    props: IBuyboxViewProps & IBuyboxExtentionProps<IBuyboxExtData>,
    attributeName:string
    ): string | undefined | number {
    const productAttributes = props.data.productAttributes.result;
    
    let maxIntesnity: string | number | undefined;
    if (!productAttributes) {
        return undefined;
    }

    let intensityValue: string | number | undefined;
    ArrayExtensions.validValues(
        productAttributes.map(item => {
             if (item.Name === attributeName.toUpperCase() && item.TextValue !== '') {
                intensityValue = item.TextValue;
            }
        })
    );   
    maxIntesnity = intensityValue;  
    return maxIntesnity;   
}
export function getProductIntensity(
    props: IBuyboxViewProps & IBuyboxExtentionProps<IBuyboxExtData>,
    attributeName:string
    ): React.ReactElement | undefined {
    const productAttributes = props.data.productAttributes.result;

    let intensityValue: string |number | undefined;
    

    if (!productAttributes) {
        return undefined;
    }    

    let maxIntensity: number | string | undefined;
    maxIntensity = getMaxIntensity(props, MAX_INTENSITY) 
    if (maxIntensity &&  typeof maxIntensity === 'string' && maxIntensity.includes('Flavoured')) {
        return (
            <div className='coffee-intensity-scale'>
                <span className='coffee-intensity-label'>Flavoured</span>
            </div>
        );
    }

    ArrayExtensions.validValues(
        productAttributes.map(item => {
             if (item.Name === attributeName.toUpperCase() && item.TextValue !== '') {
                intensityValue = item.TextValue;
            }
        })
    );   

    if (intensityValue) {              
        const totalBars = parseInt(maxIntensity?.toString() ?? "0", 10);        
        const highlightCount = Math.min(parseInt(intensityValue.toString(), 10), totalBars);
        const validatedHighlightCount = Math.max(1, Math.min(totalBars, highlightCount));

        const tableCells = Array.from({ length: totalBars }, (_, index) => (
            <td
                key={index}
                className={`intensity-cell ${index < validatedHighlightCount ? 'highlighted' : ''}`}
            >
                <div className="bar"></div>
            </td>
        ));
        return (
            <div className={`msc-product-custom-attribute-${attributeName.toLowerCase()}`}>    
            <table className="intensity-table">                
                <tbody>
                 <tr>     
                    <span className='intensity-label'>Intensity {intensityValue}</span>        
                    {tableCells}                   
                </tr>
                </tbody>
            </table>
        </div>           
        );

    }
    return undefined;    
}

export function getProductAttribute(
    props: IBuyboxViewProps & IBuyboxExtentionProps<IBuyboxExtData>,
    attributeName:string
    ): React.ReactElement | undefined {
    const productAttributes = props.data.productAttributes.result;
    // const productIntensiy = "Intensity";
    let customAttributeValue: string | undefined;
    if (!productAttributes) {
        return undefined;
    }
    ArrayExtensions.validValues(
        productAttributes.map(item => {
             if (item.Name === attributeName.toUpperCase() && item.TextValue !== '') {
                customAttributeValue = item.TextValue;
            }
        })
    );   
    if(!customAttributeValue) {
        return undefined;
    }
    //remove blank space, if any.
    const _attributeName = attributeName.replace(/\s+/g, '');  
    
    return customAttributeValue ? (
        <div className={`msc-product-custom-attribute-${_attributeName.toLowerCase()}`}>    
            {customAttributeValue}
        </div>
    ) : undefined;
}
// end comments 
// const _renderTextBlock = (textBlocks: React.ReactNode[]): JSX.Element | undefined => {
//     if (!textBlocks || textBlocks.length === 0) {
//         return undefined;
//     }

//     return <>{textBlocks[0]}</>;
// };

const _renderSocialShare = (socialShare: React.ReactNode[]): JSX.Element | undefined => {
    if (!socialShare || socialShare.length === 0) {
        return undefined;
    }
    return <>{socialShare[0]}</>;
};

const _renderCartAndActions = (
    addToCart?: IBuyboxAddToCartViewProps,
    addToOrderTemplate?: IBuyboxAddToOrderTemplateViewProps,
    addToWishlist?: IBuyboxAddToWishlistViewProps
): JSX.Element | undefined => {
    if (!addToCart && !addToWishlist) {
        return undefined;
    }

    return (
        <div className='product-add-to-cart'>
            {addToCart && addToCart.errorBlock}
            {addToOrderTemplate && addToOrderTemplate.errorBlock}
            {addToWishlist && addToWishlist.errorBlock}
            <div className='buttons'>
                {addToCart && addToCart.button}
                {addToOrderTemplate && addToOrderTemplate.button}
                {addToWishlist && addToWishlist.button}
            </div>
        </div>
    );
};

const _renderConfigure = (configure: IBuyboxProductConfigureViewProps): JSX.Element => {
    const { ContainerProps, dropdowns } = configure;

    return <Node {...ContainerProps}>{dropdowns.map(_renderConfigureDropdown)}</Node>;
};

const _renderConfigureDropdown = (dropdown: IBuyboxProductConfigureDropdownViewProps): JSX.Element => {
    const { ContainerProps, LabelContainerProps, heading, errors, select } = dropdown;

    return (
        <Node {...ContainerProps}>
            <Node {...LabelContainerProps}>
                {heading}
                {errors}
            </Node>
            {select}
        </Node>
    );
};

const _renderFindInStore = (findInStore: IBuyboxFindInStoreViewProps): JSX.Element => {
    const { ContainerProps, storeSelector, heading, description, errors, button, modal, productPickupOptionList } = findInStore;

    return (
        <Node {...ContainerProps}>
            {storeSelector}
            {heading}
            {productPickupOptionList}
            {description}
            {errors}
            {button}
            {modal}
        </Node>
    );
};

/**
 * On Change function.
 * @param callbacks -Callbacks.
 * @returns Update quantity.
 */
const onChangeHandler = (callbacks: IBuyboxCallbacks) => (newValue: number): boolean => {
    if (callbacks.updateQuantity) {
        return callbacks.updateQuantity(newValue);
    }
    return true;
};

const _renderQuantity = (
    quantityComponent: IBuyboxProductQuantityViewProps,
    callbacks: IBuyboxCallbacks,
    props: IBuyboxProps<IBuyboxData>,
    state: IBuyboxState,
    extentionResources: IBuyboxExtentionResources,
    quantityLimitsMessages: React.ReactNode,
    min: number | undefined,
    max: number | undefined,
    applyDefaultOrderSettings?: boolean,
    telemetryContent?: ITelemetryContent
): JSX.Element => {
    const { ContainerProps, LabelContainerProps, heading, errors } = quantityComponent;

    const { resources } = props;

    const { quantity } = state;

    return (
        <Node {...ContainerProps}>
            <Node {...LabelContainerProps}>
                {heading}
                {errors}
            </Node>

            <IncrementalQuantity
                id='ms-buybox__product-quantity-input'
                min={min}
                max={max}
                applyDefaultOrderSettings={applyDefaultOrderSettings}
                currentCount={quantity}
                onChange={onChangeHandler(callbacks)}
                inputQuantityAriaLabel={resources.inputQuantityAriaLabel}
                decrementButtonAriaLabel={extentionResources.decrementButtonAriaLabel}
                incrementButtonAriaLabel={extentionResources.incrementButtonAriaLabel}
                minQuantityText={extentionResources.minQuantityText}
                maxQuantityText={extentionResources.maxQuantityText}
                telemetryContent={telemetryContent}
            />
            {quantityLimitsMessages}
        </Node>
    );
};

const _renderKeyInPrice = (keyInPrice: IBuyboxKeyInPriceViewProps): JSX.Element => {
    const { ContainerProps, LabelContainerProps, heading, input } = keyInPrice;

    return (
        <Node {...ContainerProps}>
            <Node {...LabelContainerProps}>{heading}</Node>
            {input}
        </Node>
    );
};

const _renderShopSimilarItem = (shopSimilarItem: IBuyboxShopSimilarLookViewProps): JSX.Element => {
    const { ContainerProps, errors, input } = shopSimilarItem;

    return (
        <Node {...ContainerProps}>
            {errors}
            {input}
        </Node>
    );
};

export default BuyboxView;
